/**
 * A class for handling sending a vehicle reservation to Kateetti API.
 */
import LeadHelper from './lead-helper';
class KateettiReservation {
  /**
   * @constructor
   * @param {Element} element - The form element to be handled.
   *
   */
  constructor(element) {
    this.form = element;
    this.openDialogButton = document.querySelector('button[data-target="reserve-vehicle-modal"]');
    this.smallPrint = this.form.querySelector('.reserve-vehicle__small-print');
    this.submitButton = this.form.querySelector('.reserve-vehicle__submit');
    this.closeButton = this.form.querySelector('.reserve-vehicle__close');
    this.errorElem = this.form.querySelector('.reserve-vehicle__error');
    this.buttons = this.form.querySelector('.reserve-vehicle__buttons');
    this.loadingElem = this.form.querySelector('.reserve-vehicle__loading');
    this.regNumberCheckbox = this.form.querySelector('input[name="have-trade-in-car"]');
    this.regNumberInput = this.form.querySelector('input[name="trade-in-car-regnumber"]');
    this.regNumberInputContainer = this.form.querySelector('.form-field__regnumber');
    this.asiaKoskee = this.form.querySelector('input[name="asia_koskee"]'); // @see {https://crasman.atlassian.net/browse/JRINTCONT-1136, https://crasman.atlassian.net/browse/JRINTCONT-1148}
    this.errorElem.innerHTML = '';
    this.errorElem.style.display = 'none';

    this.form.addEventListener('submit', this.#reserveVehicle.bind(this));
    this.regNumberCheckbox.addEventListener('change', this.#onTradeInCarCheckboxChange.bind(this));
    this.openDialogButton.addEventListener('click', this.#handleOpenDialogButtonClick.bind(this));
  }

  #onTradeInCarCheckboxChange(event) {
    this.regNumberInputContainer.classList.toggle('hidden', !event.target.checked);
    this.regNumberInput.disabled = !event.target.checked;
  }

  /**
   * Handle the click event on the open dialog button.
   * This function is used to set the value of the "asia_koskee" input field
   * based on the button click source and whether a 360 image is present.
   *
   * @param {Event} event - The click event of the button.
   */
  #handleOpenDialogButtonClick(event) {
    const asiaKoskeeArray = [];
    // JRINTCONT-1148: check if button was clicked via chat box or via website
    const buttonClickSourceText = event.isTrusted ? 'Varausmaksu, verkkosivu' : 'Varausmaksu, chat';
    asiaKoskeeArray.push(buttonClickSourceText);
    // JRINTCONT-1136: check if 360 image is present
    if (document.querySelector('.images-360__container')) asiaKoskeeArray.push('360kuva');

    // Get interesting values from cookies
    LeadHelper.parseAsiaKoskeeFromCookies().forEach(item => asiaKoskeeArray.push(item));

    this.asiaKoskee.value = asiaKoskeeArray.join(' | ');
    // console.log('asia_koskee', this.asiaKoskee.value);
  }

  /**
   *
   * Get the data from the form and send it to the Kateetti API.
   * If response is successful, redirect to the URL provided in the response,
   * otherwise show an error message.
   *
   * @param {Event} event - The submit event of the form.
   */
  async #reserveVehicle(event) {
    event.preventDefault();

    const formData = new FormData(this.form);
    const data = {};

    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }

    this.#disableFormElements();
    this.smallPrint.style.display = 'none';
    this.loadingElem.style.display = 'block';

    try {
      const response = await this.#sendReservationData(data);
      const responseJSON = await response.json();

      if (responseJSON.status === 'error') {
        this.errorElem.style.display = 'flex';
        this.errorElem.innerHTML = `${responseJSON.msg}`;
        this.loadingElem.style.display = 'none';
        this.#enableCloseButton();
        return;
      }
      // redirect
      if (responseJSON.url) {
        window.location.href = responseJSON.url;
      }
    } catch (error) {
      console.warn('Error reserving vehicle', error);
    }
    this.#enableFormElements();
    this.loadingElem.style.display = 'none';
  }

  /**
   * Send vehicle reservation data to Kateetti API
   * @param {Object} data of the form to be sent to the API
   * @returns {Promise} - The response from the API
   */
  async #sendReservationData(data) {
    return await fetch('/stage-ajax/reserve-vehicle', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  }

  /**
   * Enable form input elements and show submit / cancel buttons.
   */
  #enableFormElements() {
    Array.from(this.form.elements).forEach(formElement => (formElement.disabled = false));
    this.buttons.style.display = 'flex';
  }

  /**
   * Disable form input elements and hide submit / cancel buttons.
   */
  #disableFormElements() {
    Array.from(this.form.elements).forEach(formElement => (formElement.disabled = true));
    this.buttons.style.display = 'none';
  }

  /**
   * Enable close button after error state.
   */
  #enableCloseButton() {
    this.buttons.style.display = 'flex';
    this.closeButton.disabled = false;
    this.submitButton.style.display = 'none';
  }
}

export default KateettiReservation;
